import React, { useState, useCallback, useEffect } from 'react'

import { authService } from '@platform/auth/services/auth.service'
import {
  LoginData,
  transformUserResponse,
  userWithoutRoleError,
} from '@platform/auth/types/ui.types'
import { useTranslation } from '@platform/i18n/hooks/useTranslation'
import { useZodForm } from '@shared/hooks/useZodForm'
import { mapServerErrorsToForm } from '@shared/utils/form-error-mapper'
import type { SubmitHandler } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod/v4'

import { useAuth } from '@/shared/contexts/AuthContext'
import { useTheme } from '@/shared/hooks/useTheme'
import { ApiException, toBackendMessage } from '@/shared/utils/api'
import { logger } from '@/shared/utils/logger'

/**
 * Login Form Component
 * Handles user authentication with email and password.
 * Uses react-hook-form + Zod for validation.
 *
 * NOTE on password validation:
 * The `password` field is intentionally `min(1)` — just "non-empty". This is
 * DIFFERENT from the `passwordFieldForCreation` factory in `@bat/shared`
 * (which enforces `PASSWORD_MIN_LENGTH = 8` for new/reset passwords).
 * Rationale:
 *   - Login validation should accept ANY non-empty password; the server is
 *     the authority on whether it matches the stored hash.
 *   - Enforcing `min(8)` client-side would reject legacy accounts whose
 *     passwords pre-date the current policy AND would leak the current policy
 *     to attackers via the login form.
 *   - The 8-char policy lives in `PASSWORD_MIN_LENGTH` (shared) and is used
 *     consistently by `CreateUserModal`, `ResetPasswordPage`, etc., so it is
 *     a single source of truth.
 *
 * The i18n keys (`validation.passwordRequired`, etc.) are flat keys in
 * `auth.json` — they do NOT follow the `${prefix}.required` convention that
 * the shared `*Field` factories emit, which is why we keep the inline Zod
 * call here instead of using `passwordFieldForLogin`.
 */

const loginSchema = z.object({
  email: z.string().min(1, 'validation.emailRequired').email('validation.invalidEmail'),
  // Deliberately `min(1)` — see header comment. Server validates the actual
  // password.
  password: z.string().min(1, 'validation.passwordRequired'),
  rememberMe: z.boolean().optional(),
})

type LoginFormValues = z.infer<typeof loginSchema>

interface LoginFormProps {
  onSuccess?: () => void
  onSwitchToRegister?: () => void
  className?: string
  showSwitchToRegister?: boolean
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
  onSwitchToRegister,
  className = '',
  showSwitchToRegister = true,
}) => {
  const { t } = useTranslation('auth')
  const { t: tErr } = useTranslation('errors')
  const [showPassword, setShowPassword] = useState(false)
  const [globalError, setGlobalError] = useState<string | null>(null)
  const [mfaRequired, setMfaRequired] = useState(false)
  const [twoFactorRequiredNotConfigured, setTwoFactorRequiredNotConfigured] = useState(false)
  const [pendingEmail, setPendingEmail] = useState('')
  // Pre-auth token issued by /auth/login when MFA is required. The challenge
  // endpoint REQUIRES it (in the body or the mfaPreAuth cookie) to mint a session.
  const [pendingPreAuthToken, setPendingPreAuthToken] = useState<string | undefined>(undefined)
  const [mfaCode, setMfaCode] = useState('')
  const [useBackupCode, setUseBackupCode] = useState(false)
  const { isLoading: authIsLoading, error: authError, clearError, setAuthenticatedUser } = useAuth()
  const { isDark } = useTheme()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
    reset,
  } = useZodForm({
    schema: loginSchema,
    defaultValues: { email: '', password: '', rememberMe: false },
  })

  const isLoading = isSubmitting || authIsLoading

  // Theme-aware classes
  const labelClasses = isDark ? 'text-crypto-text' : 'text-gray-700'
  const inputClasses = isDark
    ? 'bg-crypto-dark-lighter border-crypto-border text-crypto-text placeholder-crypto-muted'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
  const mutedClasses = isDark ? 'text-crypto-muted' : 'text-gray-600'

  // Clear auth-context error on unmount
  useEffect(() => {
    return () => {
      clearError()
    }
  }, [clearError])

  // Sync auth-context error into global error state
  useEffect(() => {
    if (authError) {
      const msg = typeof authError === 'string' ? authError : toBackendMessage(authError, tErr)
      setGlobalError(msg)
    } else {
      setGlobalError(null)
    }
  }, [authError, tErr])

  const onSubmit: SubmitHandler<LoginFormValues> = useCallback(
    async (values: LoginFormValues) => {
      setGlobalError(null)
      setTwoFactorRequiredNotConfigured(false)
      clearError()

      const credentials: LoginData = {
        email: values.email.trim(),
        password: values.password,
        rememberMe: values.rememberMe ?? false,
      }

      try {
        // Use MFA-aware login
        const result = await authService.loginWithMfa(credentials)

        // Check if MFA is required
        if ('mfaRequired' in result && result.mfaRequired) {
          setMfaRequired(true)
          setPendingEmail(values.email.trim())
          setPendingPreAuthToken(result.preAuthToken)
          return
        }

        // Normal login success - sync AuthContext so redirect fires
        if ('user' in result) {
          const signedIn = transformUserResponse(result.user)
          if (!signedIn) {
            // The service stored the session before the role was checked: close it (and revoke it on the server).
            await authService.logout()
            throw userWithoutRoleError()
          }
          setAuthenticatedUser(signedIn)
          reset()
          onSuccess?.()
        }
      } catch (err: unknown) {
        logger.error('Login error:', err)
        // Errors arrive in two shapes: ApiException thrown by authService
        // (code/message/details at top level) or a legacy `{ error: {...} }`
        // envelope. Normalize both ONCE so code checks and field mapping work.
        const legacyErr = err as { error?: { details?: unknown; message?: string; code?: string } }
        const normalized: {
          code?: string | undefined
          message?: string | undefined
          details?: unknown
        } =
          err instanceof ApiException
            ? { code: err.code, message: err.message, details: err.details }
            : (legacyErr.error ?? {})
        const backendCode = normalized.code
        const backendMessage = normalized.message
        // Pass details when it is an array (Zod field errors) OR a plain
        // object (Record<field, message> map) — for BOTH shapes of error.
        // mapServerErrorsToForm itself now rejects error-envelope objects
        // ({ code, message, ... }), so envelope-shaped details can no longer
        // fake a field mapping and suppress the global error banner
        // (invalid-credentials-shows-nothing regression).
        const backendDetails =
          Array.isArray(normalized.details) ||
          (typeof normalized.details === 'object' && normalized.details !== null)
            ? normalized.details
            : undefined

        // Check for TWO_FACTOR_REQUIRED error
        if (backendCode === 'TWO_FACTOR_REQUIRED') {
          setTwoFactorRequiredNotConfigured(true)
          setGlobalError(
            typeof backendMessage === 'string' && backendMessage.length > 0
              ? backendMessage
              : toBackendMessage(err, tErr),
          )
          return
        }

        const mapped = mapServerErrorsToForm<LoginFormValues>(
          {
            error: { code: backendCode, message: backendMessage, details: backendDetails },
          } as Parameters<typeof mapServerErrorsToForm>[0],
          setError,
        )
        if (!mapped) {
          // toBackendMessage guarantees a friendly, localized message — raw
          // technical codes (e.g. `bad_response`) are never shown to the user.
          setGlobalError(toBackendMessage(err, tErr))
        }
      }
    },
    [onSuccess, clearError, setError, reset, setAuthenticatedUser, tErr],
  )

  // Submit MFA code
  const submitMfaCode = useCallback(async () => {
    if (!mfaCode || mfaCode.length < 6) {
      setGlobalError(t('mfa.invalid_code_format', 'Please enter a valid code'))
      return
    }

    setGlobalError(null)

    try {
      const result = await authService.mfaChallenge(
        pendingEmail,
        mfaCode,
        useBackupCode,
        pendingPreAuthToken,
      )

      if ('user' in result) {
        const signedIn = transformUserResponse(result.user)
        if (!signedIn) {
          // The service stored the session before the role was checked: close it (and revoke it on the server).
          await authService.logout()
          throw userWithoutRoleError()
        }
        setAuthenticatedUser(signedIn)
        setMfaRequired(false)
        setMfaCode('')
        setPendingEmail('')
        setPendingPreAuthToken(undefined)
        reset()
        onSuccess?.()
      }
    } catch (err: unknown) {
      logger.error('MFA challenge error:', err)
      setGlobalError(toBackendMessage(err, tErr))
    }
  }, [
    pendingEmail,
    mfaCode,
    useBackupCode,
    pendingPreAuthToken,
    onSuccess,
    t,
    tErr,
    reset,
    setAuthenticatedUser,
  ])

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`space-y-6 ${className}`}
      noValidate
      data-testid="login-form"
    >
      {/* Email Field */}
      <div>
        <label htmlFor="login-email" className={`block text-sm font-medium mb-2 ${labelClasses}`}>
          {t('fields.email_address')}
        </label>
        <input
          id="login-email"
          type="email"
          autoComplete="email"
          {...register('email')}
          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${inputClasses} ${
            errors.email
              ? 'border-red-500 focus:ring-red-500'
              : 'focus:ring-crypto-accent focus:border-crypto-accent'
          }`}
          placeholder={t('placeholders.enterEmail')}
          disabled={isLoading}
          data-testid="login-email-input"
        />
        {errors.email && (
          <p
            className="mt-1 text-sm text-red-500 [html[data-theme=light]_&]:text-red-700"
            data-testid="login-email-error"
          >
            {t(
              typeof errors.email.message === 'string'
                ? errors.email.message
                : 'validation.invalidEmail',
            )}
          </p>
        )}
      </div>

      {/* Password Field */}
      <div>
        <label
          htmlFor="login-password"
          className={`block text-sm font-medium mb-2 ${labelClasses}`}
        >
          {t('fields.password')}
        </label>
        <div className="relative">
          <input
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            {...register('password')}
            className={`w-full px-4 py-3 pr-12 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${inputClasses} ${
              errors.password
                ? 'border-red-500 focus:ring-red-500'
                : 'focus:ring-crypto-accent focus:border-crypto-accent'
            }`}
            placeholder={t('placeholders.enterPassword')}
            disabled={isLoading}
            data-testid="login-password-input"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${
              isDark
                ? 'text-crypto-text-secondary hover:text-crypto-text'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            disabled={isLoading}
            aria-label={showPassword ? t('actions.hidePassword') : t('actions.showPassword')}
            data-testid="login-password-toggle"
          >
            {showPassword ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            )}
          </button>
        </div>
        {errors.password && (
          <p
            className="mt-1 text-sm text-red-500 [html[data-theme=light]_&]:text-red-700"
            data-testid="login-password-error"
          >
            {t(
              typeof errors.password.message === 'string'
                ? errors.password.message
                : 'validation.passwordRequired',
            )}
          </p>
        )}
      </div>

      {/* Remember Me & Forgot Password */}
      <div className="flex items-center justify-between">
        <label className="flex items-center">
          <input
            type="checkbox"
            {...register('rememberMe')}
            className={`h-4 w-4 text-crypto-accent rounded focus:ring-crypto-accent focus:ring-2 ${
              isDark ? 'border-crypto-border' : 'border-gray-300'
            }`}
            disabled={isLoading}
            data-testid="login-remember-me"
          />
          <span className={`ml-2 text-sm ${mutedClasses}`}>{t('fields.remember_me')}</span>
        </label>

        <button
          type="button"
          className="text-sm text-crypto-accent hover:text-crypto-accent-light transition-colors"
          disabled={isLoading}
          onClick={() => navigate('/forgot-password')}
          data-testid="login-forgot-password"
        >
          {t('actions.forgot_password')}
        </button>
      </div>

      {/* Global Error */}
      {globalError && !twoFactorRequiredNotConfigured && (
        <div
          className="p-3 bg-red-900/20 border border-red-500/30 rounded-lg"
          data-testid="login-global-error"
          role="alert"
          aria-live="assertive"
        >
          <p className="text-sm text-red-400 [html[data-theme=light]_&]:text-red-700">
            {globalError}
          </p>
        </div>
      )}

      {/* Two-Factor Required but not configured warning */}
      {twoFactorRequiredNotConfigured && (
        <div
          className="p-4 bg-amber-900/20 border border-amber-500/30 rounded-lg"
          data-testid="login-two-factor-required-error"
        >
          <div className="flex items-start gap-3">
            <svg
              className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-amber-500 mb-1">
                {t('twoFactor.required_title', 'Two-Factor Authentication Required')}
              </h4>
              <p className="text-sm text-amber-200 [html[data-theme=light]_&]:text-amber-800">
                {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- display fallback: empty-string error must show the default message */}
                {globalError ||
                  t(
                    'twoFactor.required_message',
                    'Your organization requires two-factor authentication, but it is not enabled on your account. Please contact your administrator to enable 2FA.',
                  )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* MFA Required Section */}
      {mfaRequired && (
        <div className="space-y-4 p-4 bg-crypto-accent/10 border border-crypto-accent/30 rounded-lg">
          <div className="flex items-center gap-2">
            <svg
              className="w-5 h-5 text-crypto-accent"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            <p className="text-sm font-medium text-crypto-accent">
              {t('mfa.required_title', 'Two-Factor Authentication Required')}
            </p>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t(
              'mfa.required_desc',
              'Enter the code from your authenticator app or a backup code to complete login.',
            )}
          </p>

          {/* Code type toggle */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setUseBackupCode(false)
                setMfaCode('')
              }}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                !useBackupCode
                  ? 'bg-crypto-accent text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              {t('mfa.authenticator_app', 'Authenticator App')}
            </button>
            <button
              type="button"
              onClick={() => {
                setUseBackupCode(true)
                setMfaCode('')
              }}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                useBackupCode
                  ? 'bg-crypto-accent text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              {t('mfa.backup_code', 'Backup Code')}
            </button>
          </div>

          {/* MFA Code Input */}
          <div>
            <label htmlFor="mfa-code" className={`block text-sm font-medium mb-2 ${labelClasses}`}>
              {useBackupCode
                ? t('mfa.backup_code_label', 'Backup Code')
                : t('mfa.totp_label', 'Authentication Code')}
            </label>
            <input
              id="mfa-code"
              type="text"
              // Backup codes are alphanumeric (`WCUL-WVVM`): a numeric keypad would make
              // them impossible to type on a phone. Only the authenticator code is digits.
              inputMode={useBackupCode ? 'text' : 'numeric'}
              autoCapitalize={useBackupCode ? 'characters' : 'off'}
              spellCheck={false}
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.trim())}
              // The real shape of a backup code (XXXX-XXXX), as shown when 2FA is enabled.
              placeholder={useBackupCode ? 'XXXX-XXXX' : '000000'}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors text-center text-lg tracking-widest font-mono ${inputClasses} focus:ring-crypto-accent focus:border-crypto-accent`}
              disabled={isLoading}
              data-testid="login-mfa-input"
              autoComplete="one-time-code"
            />
          </div>

          {/* Verify button */}
          <button
            type="button"
            onClick={submitMfaCode}
            disabled={isLoading || !mfaCode}
            className="w-full py-2 px-4 bg-crypto-accent hover:bg-crypto-accent-light disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white disabled:text-gray-500 rounded-lg font-medium transition-colors"
          >
            {isLoading ? t('mfa.verifying', 'Verifying...') : t('mfa.verify', 'Verify')}
          </button>

          {/* Cancel MFA */}
          <button
            type="button"
            onClick={() => {
              setMfaRequired(false)
              setMfaCode('')
              setPendingPreAuthToken(undefined)
            }}
            className="w-full py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            {t('actions.cancel', 'Cancel')}
          </button>
        </div>
      )}

      {!mfaRequired && (
        <>
          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-crypto-accent ${
              isLoading
                ? 'bg-crypto-muted text-crypto-dark cursor-not-allowed'
                : 'bg-crypto-accent hover:bg-crypto-accent-light text-white'
            }`}
            data-testid="login-submit-button"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                {t('modal.signingIn')}
              </div>
            ) : (
              t('actions.sign_in')
            )}
          </button>

          {/* Switch to Register */}
          {showSwitchToRegister && onSwitchToRegister && (
            <div className="text-center">
              <p className={`text-sm ${mutedClasses}`}>
                {t('modal.noAccount')}{' '}
                <button
                  type="button"
                  onClick={onSwitchToRegister}
                  className="text-crypto-accent hover:text-crypto-accent-light font-medium transition-colors"
                  disabled={isLoading}
                  data-testid="login-switch-to-register"
                >
                  {t('actions.sign_up')}
                </button>
              </p>
            </div>
          )}
        </>
      )}
    </form>
  )
}
